import React, { FC } from 'react';
import MuiTooltip from '@mui/material/Tooltip';
import translate from '@translate';
import { AutocompleteInputChangeReason } from '@mui/material/Autocomplete';

import { ListOnItemsRenderedProps } from 'react-window';
import Autocomplete from './Autocomplete';
import SimpleSelect from './SimpleSelect';
import { SxProps, Theme } from '@mui/material/styles';

const VIRTUALIZED_FROM_LENGTH = 100;

export type SelectValueType = string | number | null | boolean | undefined;

export interface OptionType {
    label: string;
    value: SelectValueType;
    color?: string;
}

export type OnItemsRenderedType = (props: ListOnItemsRenderedProps) => any;

export type OnInputChangeType = (
    event: React.SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason
) => any;

export type OnChangeType = (value: SelectValueType | SelectValueType[], error: boolean) => void;

export interface SelectProps {
    value: any | any[];
    onChange?: OnChangeType;
    variant?: 'filled' | 'outlined' | 'standard';
    size?: 'small' | 'medium';
    error?: boolean;
    required?: boolean;
    disabled?: boolean;
    fullWidth?: boolean;
    multiple?: boolean;
    portal?: boolean;
    virtualized?: boolean;
    label?: any;
    name?: string;
    tooltiop?: string;
    helperText?: string;
    placeholder?: string;
    minWidth?: number;
    options: OptionType[];
    editableOptions?: OptionType[];
    limitTags?: number; //pocet max zobrazenych pri multiple
    onItemsRendered?: OnItemsRenderedType;
    onInputChange?: OnInputChangeType;
    onFocus?: () => void;
    onBlur?: () => void;
    emptyOption?: boolean;
    emptyOptionValue?: any;
    sx?: SxProps<Theme>;
    loading?: boolean;
    optionsExpandWidth?: boolean;
    disableVirtualized?: boolean;
    optionStyleFce?: (option: OptionType) => SxProps<Theme> | null;
    expandList?: boolean;
    inputSx?: SxProps<Theme> | null;
}

const Select: FC<SelectProps> = (props) => {
    const {
        value,
        onChange,
        variant,
        size,
        error,
        required,
        fullWidth,
        multiple,
        label,
        name,
        tooltiop,
        options,
        limitTags,
        portal,
        virtualized,
        minWidth,
        emptyOption,
        emptyOptionValue,
        onBlur,
        onFocus,
        onInputChange,
        onItemsRendered,
        disabled,
        sx,
        loading,
        optionsExpandWidth,
        disableVirtualized,
        optionStyleFce,
        expandList,
        placeholder,
        inputSx,
    } = props;

    // emptyOption && console.log(emptyOptionValue, value);
    const editableOptions = [
        ...(emptyOption ? [{ label: '-', value: undefined || emptyOptionValue }] : []),
        ...(props.editableOptions || options),
    ];

    if (props.editableOptions && !props.editableOptions?.find((option) => option.value === props.value)) {
        const addOption = props.options.find((option) => option.value === props.value);
        if (addOption) {
            editableOptions.push(addOption);
        }
    }

    const isDisabled = disabled || (!virtualized && editableOptions.length < 1);

    let errorText = '';
    if (error) {
        if (required) {
            errorText = translate('textField.requiredError');
        } else {
            errorText = translate('textField.error');
        }
    }
    const helperText = errorText || props.helperText || ' ';

    const isAutocomplete = multiple || virtualized || (editableOptions || options).length > VIRTUALIZED_FROM_LENGTH;

    const componentRender = isAutocomplete ? (
        <Autocomplete
            value={value}
            onChange={onChange}
            variant={variant}
            size={size}
            error={error}
            required={required}
            fullWidth={fullWidth}
            multiple={multiple}
            label={label}
            name={name}
            helperText={helperText}
            options={editableOptions}
            limitTags={limitTags}
            portal={portal}
            virtualized={virtualized}
            minWidth={minWidth}
            onBlur={onBlur}
            onFocus={onFocus}
            onInputChange={onInputChange}
            onItemsRendered={onItemsRendered}
            disabled={isDisabled}
            loading={loading}
            optionsExpandWidth={optionsExpandWidth}
            disableVirtualized={disableVirtualized}
            optionStyleFce={optionStyleFce}
            inputSx={inputSx}
            expandList={expandList}
            placeholder={placeholder}
        />
    ) : (
        <SimpleSelect
            value={value}
            onChange={onChange}
            variant={variant}
            size={size}
            error={error}
            required={required}
            fullWidth={fullWidth}
            label={label}
            name={name}
            helperText={helperText}
            options={editableOptions}
            minWidth={minWidth}
            disabled={isDisabled}
            sx={sx}
            optionStyleFce={optionStyleFce}
        />
    );

    if (tooltiop) {
        return <MuiTooltip title={tooltiop}>{componentRender}</MuiTooltip>;
    }

    return componentRender;
};

export default Select;
